import styled from "@emotion/styled"
import { PIcon } from "@porsche-design-system/components-react"
import {
	colorFocus,
	colorFrosted,
	durationSm,
	easeInOut,
	fontPorscheNext,
	fontWeightSemibold,
	radiusSm,
	spacingStaticXs,
	typescaleXs,
} from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Flex } from "../Flex"

export interface TableSortingButtonProps {
	direction?: "ASC" | "DESC"
	onClick?: () => void
	readonly?: boolean
}

export const TableSortingButton = ({
	children,
	direction,
	readonly,
	...rest
}: PropsWithChildren<TableSortingButtonProps>) => {
	const icon = (
		<StyledTableSortingButtonIcon name="arrow-up" size="xs" $active={!!direction} $direction={direction ?? "ASC"} />
	)

	return readonly ? (
		<Flex $gap="xs" $alignItems="flex-end">
			{children}
			{icon}
		</Flex>
	) : (
		<StyledTableSortingButton {...rest}>
			{children}
			{icon}
		</StyledTableSortingButton>
	)
}

/* eslint-disable perfectionist/sort-objects */
// source: https://github.com/porsche-design-system/porsche-design-system/blob/7c01d757318b4f99e89c31c5c01146969f10a160/packages/components/src/components/table/table-head-cell/table-head-cell-styles.ts#L100-L104
const StyledTableSortingButtonIcon = styled(PIcon, {
	shouldForwardProp,
})<{ $active?: boolean; $direction?: "ASC" | "DESC" }>(({ $active, $direction }) => ({
	transition: `opacity ${durationSm} ${easeInOut}`,
	opacity: $active ? 1 : 0,
	transform: `rotate3d(0,0,1,${$direction === "ASC" ? 0 : 180}deg)`,
	transformOrigin: "50% 50%", // for iOS
}))

// source: https://github.com/porsche-design-system/porsche-design-system/blob/7c01d757318b4f99e89c31c5c01146969f10a160/packages/components/src/components/table/table-head-cell/table-head-cell-styles.ts#L50-L87
const StyledTableSortingButton = styled.button({
	position: "relative",
	display: "flex",
	gap: spacingStaticXs,
	width: "auto",
	margin: 0, // Removes default button margin on safari 15
	padding: 0,
	fontFamily: fontPorscheNext,
	fontSize: typescaleXs,
	fontWeight: fontWeightSemibold,
	lineHeight: "inherit",
	color: "inherit",
	alignItems: "flex-end",
	WebkitAppearance: "none", // iOS safari
	appearance: "none",
	background: "transparent",
	textAlign: "start",
	border: 0,
	zIndex: 0,
	cursor: "pointer",
	"&::before": {
		content: '""',
		position: "absolute",
		inset: "-2px -4px",
		borderRadius: radiusSm,
		zIndex: -1, // needed so that text behind element is selectable and/or visible
		transition: `background-color ${durationSm} ${easeInOut}`,
	},
	"&:focus-visible::before": {
		outline: `2px solid ${colorFocus}`,
		outlineOffset: "2px",
	},
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover, &:focus-visible": {
			[StyledTableSortingButtonIcon as any]: {
				opacity: 1,
			},
		},
		"&:hover::before": {
			backgroundColor: colorFrosted,
		},
	},
	outline: 0,
})
